/**
 * The realtime channel names, in one place because **two sides have to agree on them**:
 * the server publishes and the browser subscribes, and a mismatched string is a screen
 * that never updates and never errors.
 *
 * Two per show (runtime-model §3.3):
 *
 * | Channel                 | Carries                                            |
 * | ----------------------- | -------------------------------------------------- |
 * | `shows/{showId}/room`   | chat, reactions, presence, pins, show lifecycle    |
 * | `shows/{showId}/bids`   | the high-cadence bid traffic                       |
 *
 * **Split so a bid storm cannot starve chat** — the noisy-neighbor problem that drove
 * CommentSold off a previous transport, solved inside one transport rather than by adding
 * a second.
 *
 * These are paths under the `shows` channel namespace, not namespaces themselves: a
 * namespace per show would be a control-plane call on every go-live and a resource to
 * clean up after every abandoned one.
 */

export const REALTIME_NAMESPACE = 'shows';

export function roomChannel(showId: string): string {
  return `${REALTIME_NAMESPACE}/${showId}/room`;
}

export function bidsChannel(showId: string): string {
  return `${REALTIME_NAMESPACE}/${showId}/bids`;
}

/**
 * What a client may receive. **Deliberately not the domain event** — an event is the
 * system's record of what happened and carries fields a viewer has no business seeing
 * (the losing bid book, a reserve, a lease reference). A realtime message is a nudge with
 * just enough in it to render, and the client re-reads for anything more.
 */
export type RealtimeMessageKind =
  | 'bid'
  | 'extended'
  | 'closed'
  | 'lot-open'
  | 'show-state'
  | 'chat'
  /** A moderator took a message down; a client drops it rather than renumbering the room. */
  | 'chat-removed'
  /** A burst of hearts. Carries a count, because a client batches taps rather than sending one each. */
  | 'reaction';

export interface RealtimeMessage {
  kind: RealtimeMessageKind;
  /** Epoch ms, server clock — the same basis every countdown on the client already uses. */
  at: number;
  /**
   * Monotonic per auction, from the engine's `bidCount`. **A gap means a missed message**,
   * and the client refetches rather than rendering a wrong leader (runtime-model §3.4).
   * Absent on messages that are not about one auction's sequence.
   */
  sequence?: number;
  auctionId?: string;
  showId: string;
  /** Kind-specific, small, and never authoritative. */
  data?: Record<string, unknown>;
}
